import React, { useCallback, useMemo } from "react";

import { isNonEmptyObject } from "@cayuse/shared-react.utils.type-checks";

import questions from "containers/Form/SimplifiedBuilder/Questions";

import { QuestionFooter } from "./Footer";
import { QuestionHeader } from "./Header";
import { AttachmentsCustomSettings, DELETION_OPTIONS } from "../AttachmentsCustomSettings";

const stripConditionBuilderFieldIfApplicable = (newConfig, prevConfig, field) => {
	if (newConfig?.[field] !== prevConfig?.[field] && newConfig[field] === true) {
		const { [field]: ignoredItem, ...newConditionBuilderInfo } = newConfig.designInfo.conditionBuilderInfo;
		newConfig.designInfo.conditionBuilderInfo = newConditionBuilderInfo;
	}
};

const removeConditionBuilderInfoIfApplicable = (newConfig, prevConfig) => {
	if (isNonEmptyObject(newConfig.designInfo?.conditionBuilderInfo)) {
		stripConditionBuilderFieldIfApplicable(newConfig, prevConfig, "visible");
		stripConditionBuilderFieldIfApplicable(newConfig, prevConfig, "ignored");
	}
};

export const QuestionComponent = ({ completeFormModel, config, onChange, onDelete }) => {
	const onChangeConfig = useCallback(newProps => {
		onChange(prev => {
			const newConfig = { ...prev, ...newProps };
			// handle extraSettings for components
			if (newConfig.type === "number" && !newConfig.extraSettings.numberType) {
				// add extraSettings required for number component
				newConfig.extraSettings = questions["number"].extraSettings;
			}

			if (newConfig.type === "upload" && !newConfig.extraSettings.allowDelete) {
				// Set default value for allowDelete
				newConfig.extraSettings.allowDelete = DELETION_OPTIONS[0].value;
			}

			if(newProps.type && newConfig.cayuseType && newConfig.type !== newConfig.cayuseType) {
				newConfig.cayuseType = null;
				newConfig.extraSettings = {};
			}

			if(questions[newConfig.type]?.information?.cayuseType || newConfig.cayuseType) {
				const { information, extraSettings } = questions[
					newConfig.cayuseType || newConfig.type
				];

				if (!newConfig.cayuseType) {
					const { cayuseType, type } = information;
					newConfig.cayuseType = cayuseType;
					newConfig.type = type;
				}

				newConfig.extraSettings = { ...extraSettings, ...newProps.extraSettings };
			}

			removeConditionBuilderInfoIfApplicable(newConfig, prev);

			return newConfig;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const Component = useMemo(
		() => questions[config.type === "one" ? "number" : config.cayuseType || config.type],
		[ config.cayuseType, config.type ]
	);

	const CustomSettingsComponent = useMemo(
		() => config.type === "upload" ? AttachmentsCustomSettings : null,
		[ config.type ]
	);

	return (
		<>
			<QuestionHeader
				config={config}
				onChangeConfig={onChangeConfig}
			/>
			{Component && (
				<Component config={config} onChange={onChangeConfig} />
			)}
			<QuestionFooter
				completeFormModel={completeFormModel}
				config={config}
				onChangeConfig={onChangeConfig}
				onDelete={onDelete}
				CustomSettingsComponent={CustomSettingsComponent}
			/>
		</>
	);
};

QuestionComponent.showInPalette = () => true;

QuestionComponent.paletteInformation = () => ({
	id: "question",
	name: "Question",
	type: "text",
	group: "formElement"
});

QuestionComponent.newElement = () => ({
	designInfo: {
		stencilSuperIds: [
			"base-text",
			"Component"
		],
		stencilId: "cloud-text",
		customStencilId: "null"
	},
	type: "text"
});
